import { Plate } from 'components/Experiment/constants';

const getCombinedClientRect = (stageRef) => {
  stageRef.scale({ x: 1, y: 1 });
  stageRef.position({ x: 0, y: 0 });
  stageRef.batchDraw();

  const nodes = stageRef.getChildren();
  if (!nodes || nodes.length === 0) {
    return null;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let foundValidNode = false;

  nodes.forEach((node) => {
    if (node && node.getClientRect) {
      const rect = node.getClientRect();
      if (rect.width > 0 && rect.height > 0) {
        minX = Math.min(minX, rect.x);
        minY = Math.min(minY, rect.y);
        maxX = Math.max(maxX, rect.x + rect.width);
        maxY = Math.max(maxY, rect.y + rect.height);
        foundValidNode = true;
      }
    }
  });

  if (!foundValidNode) {
    return null;
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

function matchesId(nodeId: string, patterns: string[]): boolean {
  return patterns.some((pattern) => {
    if (pattern.endsWith('*')) {
      // remove the * and check prefix
      const prefix = pattern.slice(0, -1);
      return nodeId.startsWith(prefix);
    }
    return nodeId === pattern;
  });
}

export const CanvasGroups = {
  DESIGN: 'konva-group-design',
  DESIGNLAYER: 'konva-group-design-layer-*',
  DESIGNLAYERS: 'konva-group-design-layers',
  PLATEBARCODE: 'konva-group-plate-barcode',
  PLATEGRID: 'konva-group-plate-grid',
  INDICATORTEXT: 'konva-group-indicator-text',
  LAYER1: 'rmwcanvas-layer1',
  EXPLAYERSRENDERER: 'exp-layersrenderer-group',
  SCREEN: 'screen-group',
};

export function getDesignImageURL(stageRef, plate: Plate = null) {
  // NOTE: (HACK) Plate barcode and grid letters are approximately half the size of a well (60% for x-axis margin, 50% for y-axis)
  // As a hackish solution to avoid scaling issues, we use this to determine plate X and Y margin pixels
  const rect = {
    x: plate.startX - plate.wellRects[0].w * 0.6,
    y: plate.startY - plate.wellRects[0].h * 0.5,
    width: plate.width + plate.wellRects[0].w,
    height: plate.height + plate.wellRects[0].h,
  };
  const idsToShow = [
    CanvasGroups.DESIGN,
    CanvasGroups.DESIGNLAYER,
    CanvasGroups.DESIGNLAYERS,
    CanvasGroups.PLATEBARCODE,
    CanvasGroups.PLATEGRID,
    CanvasGroups.INDICATORTEXT,
    CanvasGroups.LAYER1,
    CanvasGroups.EXPLAYERSRENDERER,
    CanvasGroups.SCREEN,
  ];

  return getPlateImageURL(stageRef, idsToShow, rect);
}

export function getPlateImageURL(stageRef, idsToShow: string[] = [], rect = null) {
  if (!stageRef) {
    console.error('Stage ref is not available for screenshot.');
    return;
  }

  stageRef.batchDraw();

  // collect all groups/layers with an id
  const allGroupsAndLayers = stageRef
    .find('Group, Layer')
    .filter((node) => !!node.attrs.id);

  // save original visibility states
  const originalVisibilityStates = new Map();
  allGroupsAndLayers.forEach((node) => {
    originalVisibilityStates.set(node, node.isVisible());
  });

  // hide layers after saving original states, to avoid accidentally hiding children layers before saving their states
  allGroupsAndLayers.forEach((node) => {
    node.visible(false); // hide all by default
  });

  // selectively show nodes matching idsToShow
  allGroupsAndLayers.forEach((node) => {
    if (matchesId(node.attrs.id, idsToShow)) {
      node.visible(true);
    }
  });

  stageRef.batchDraw();

  const currentScale = stageRef.scale();
  const currentPosition = stageRef.position();
  const originalStageSize = { width: stageRef.width(), height: stageRef.height() };

  // Reset scale/position for consistent coordinates
  stageRef.scale({ x: 1, y: 1 });
  stageRef.position({ x: 0, y: 0 });
  stageRef.batchDraw();

  // If rect not provided, calculate bounding box
  const contentRect = rect ?? getCombinedClientRect(stageRef);
  if (!contentRect) {
    return null;
  }

  let resizedForCapture = false;
  if (
    contentRect.x + contentRect.width > originalStageSize.width ||
    contentRect.y + contentRect.height > originalStageSize.height
  ) {
    const newWidth = Math.max(originalStageSize.width, contentRect.x + contentRect.width);
    const newHeight = Math.max(
      originalStageSize.height,
      contentRect.y + contentRect.height,
    );
    stageRef.width(newWidth);
    stageRef.height(newHeight);
    stageRef.batchDraw();
    resizedForCapture = true;
  }

  // Capture
  const dataURL = stageRef.toDataURL({
    pixelRatio: 3,
    imageSmoothingEnabled: false,
    ...contentRect,
  });

  // restore visibility
  originalVisibilityStates.forEach((visibility, node) => {
    node.visible(visibility);
  });

  // restore original stage size if it was temporarily changed
  if (resizedForCapture) {
    stageRef.width(originalStageSize.width);
    stageRef.height(originalStageSize.height);
  }

  stageRef.scale(currentScale);
  stageRef.position(currentPosition);
  stageRef.batchDraw();

  return dataURL;
}
